/**
 * Client for the Auth Letter Agent extraction test harness.
 *
 * Lets the config editor run the real auth-letter extraction agent over an
 * uploaded PDF for a specific order and see what the agent would extract —
 * without deploying anything. The backend resolves the payer's prompt config
 * and the order's J-codes server-side; no LLM API keys are exposed to the
 * browser.
 * @module authLetterAgentTestService
 */
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { getBearerToken } from "../postCall/bearerToken";

export interface JCodeAuthInfo {
  j_code: string;
  auth_status: string;
  start_date: string | null;
  end_date: string | null;
}

export interface UnitsInfo {
  units_approved?: string | number;
  is_denial?: boolean;
  num_visits?: string | number;
  num_visits_source?: string;
}

export interface AuthLetterUploadExtraction {
  order_id: string;
  payer_name: string;
  member_id: string | null;
  provider: string | null;
  auth_number: string | null;
  icd10_code: string | null;
  source_portal: string | null;
  patient_name: string | null;
  extracted_payer_name: string | null;
  j_code_details: JCodeAuthInfo[];
  units_map: Record<string, UnitsInfo>;
  auth_initiated_date: string | null;
}

export interface RunAuthLetterUploadParams {
  payerName: string;
  orderId: string;
  orgId: string;
  medicineNames: string[];
  file: File;
}

/**
 * Run the real auth-letter extraction agent over an uploaded PDF for one order.
 * Hits the production `extract-auth-letter-upload` endpoint; the backend resolves
 * the payer's prompt config and the order's J-codes server-side.
 *
 * @input params - Payer, order, org, treatment-plan drug names, and the letter file
 * @return AuthLetterUploadExtraction - The agent's extracted letter + per-J-code details
 * @throws {Error} When the user is not authenticated or the endpoint returns a non-OK response
 */
export const runAuthLetterUploadExtraction = async (
  params: RunAuthLetterUploadParams,
): Promise<AuthLetterUploadExtraction> => {
  const token = await getBearerToken();
  if (!token) throw new Error("Not authenticated — please log in again.");
  const form = new FormData();
  form.append("file", params.file);
  form.append("payer_name", params.payerName);
  form.append("order_id", params.orderId);
  form.append("org_id", params.orgId);
  form.append(
    "medicine_names_from_treatment_plan",
    JSON.stringify(params.medicineNames),
  );
  const res = await fetch(API_ENDPOINTS.EXTRACT_AUTH_LETTER_UPLOAD, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok)
    throw new Error(`Extraction error ${res.status}: ${await res.text()}`);
  return res.json();
};
